import { useCollectionView } from '../../app/use-collection-view.js';
import {
  DateRangePicker,
  Select,
  StatusPill,
  formatCivilRange,
  todayCivilDate,
  useIsWideViewport,
} from '@hue/design-system';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { PackageCheck } from 'lucide-react';
import { materialSufficiencyApi, type MaterialSufficiencySort } from '../../lib/api.js';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  LoadingCard,
  Pagination,
  SortableColumn,
} from '../../app/data-view.js';
import type { MaterialSufficiencyView } from '../../app/list-views.js';
import type { ViewSetter } from '../../app/view-state.js';
import { getApiError } from '../../lib/api.js';
import { PageHeader } from '../../app/page-title.js';

const units = { UNIT: 'un', MILLILITER: 'ml', GRAM: 'g' } as const;

export function CompanyMaterialSufficiency({
  timeZone,
  view,
  onView,
}: {
  timeZone: string;
  view: MaterialSufficiencyView;
  onView: ViewSetter<MaterialSufficiencyView>;
}) {
  const collectionView = useCollectionView();
  const wide = useIsWideViewport() && collectionView === 'list';
  const filters = {
    ...(view.search.trim() ? { search: view.search.trim() } : {}),
    ...(view.from ? { from: view.from } : {}),
    ...(view.to ? { to: view.to } : {}),
    state: view.state,
    sort: view.sort,
    direction: view.direction,
    limit: view.limit,
    offset: view.offset,
  };
  const page = useQuery({
    queryKey: ['company', 'material-sufficiency', filters],
    queryFn: () => materialSufficiencyApi.list(filters),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const total = page.data?.total ?? 0;
  const chips = [
    ...(view.state !== 'ALL'
      ? [
          {
            key: 'state',
            label: view.state === 'SUFFICIENT' ? 'Saldo suficiente' : 'Saldo insuficiente',
            onRemove: () => onView({ state: 'ALL' }),
          },
        ]
      : []),
    ...((view.from ?? view.to)
      ? [
          {
            key: 'range',
            label: formatCivilRange({ from: view.from, to: view.to }, 'Qualquer dia'),
            onRemove: () => onView({ from: null, to: null }),
          },
        ]
      : []),
  ];
  const toggleSort = (sort: MaterialSufficiencySort) =>
    onView({ sort, direction: view.sort === sort && view.direction === 'asc' ? 'desc' : 'asc' });

  return (
    <section aria-labelledby="material-sufficiency-title">
      <PageHeader
        trail="Operação / Estoque"
        id="material-sufficiency-title"
        title="Suficiência de materiais"
        description={
          <>
            Saldo atual contra o previsto dos protocolos planejados. Esta projeção não reserva
            material e muda junto com o estoque e o planejamento.
          </>
        }
      />

      <FilterBar
        searchId="material-sufficiency-search"
        searchLabel="Buscar produto"
        searchPlaceholder="Nome do produto"
        search={view.search}
        onSearchChange={(search) => onView({ search }, { replace: true })}
        chips={chips}
        onClear={() => onView({ state: 'ALL', from: null, to: null })}
        count={`${total} ${total === 1 ? 'produto' : 'produtos'}`}
        resultsLabel={`Ver ${total} ${total === 1 ? 'produto' : 'produtos'}`}
        pageSize={view.limit}
        onPageSizeChange={(limit) => onView({ limit })}
      >
        <Select
          label="Situação"
          value={view.state}
          onValueChange={(state) => onView({ state: state as MaterialSufficiencyView['state'] })}
          options={[
            { value: 'ALL', label: 'Todas' },
            { value: 'SUFFICIENT', label: 'Saldo suficiente' },
            { value: 'INSUFFICIENT', label: 'Saldo insuficiente' },
          ]}
        />
        <div className="min-w-0">
          <span className="mb-1.5 block text-xs font-semibold text-content/70">Atendimento</span>
          <DateRangePicker
            label="Dias planejados"
            value={{ from: view.from, to: view.to }}
            onValueChange={(next) => onView({ from: next.from, to: next.to })}
            today={todayCivilDate(timeZone)}
          />
        </div>
      </FilterBar>

      {page.isLoading ? (
        <LoadingCard label="Calculando suficiência" />
      ) : page.error ? (
        <ErrorCard message={getApiError(page.error)} />
      ) : total === 0 ? (
        <EmptyCard
          icon={PackageCheck}
          title="Nenhum material previsto"
          description="Planeje protocolos com materiais para que eles entrem nesta projeção."
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-soft">
          {wide ? (
            <table className="w-full table-fixed border-collapse text-left">
              <caption className="sr-only">Suficiência de materiais planejados</caption>
              <thead>
                <tr className="border-b border-surface-border">
                  <SortableColumn
                    column="productDisplayName"
                    label="Produto"
                    {...view}
                    onToggle={toggleSort}
                  />
                  <SortableColumn
                    column="currentQuantity"
                    label="Saldo atual"
                    {...view}
                    onToggle={toggleSort}
                  />
                  <SortableColumn
                    column="plannedQuantity"
                    label="Previsto"
                    {...view}
                    onToggle={toggleSort}
                  />
                  <SortableColumn
                    column="projectedQuantity"
                    label="Saldo projetado"
                    {...view}
                    onToggle={toggleSort}
                  />
                  <th className="px-5 py-3 text-xs font-semibold text-content/70">Situação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {page.data?.items.map((row) => (
                  <tr key={row.productId}>
                    <td className="px-5 py-4 text-sm font-semibold">{row.productDisplayName}</td>
                    <Quantity value={row.currentQuantity} unit={units[row.baseUnit]} />
                    <Quantity value={row.plannedQuantity} unit={units[row.baseUnit]} />
                    <Quantity value={row.projectedQuantity} unit={units[row.baseUnit]} strong />
                    <td className="px-5 py-4">
                      <StatusPill tone={row.state === 'SUFFICIENT' ? 'positive' : 'danger'}>
                        {row.state === 'SUFFICIENT' ? 'Suficiente' : 'Insuficiente'}
                      </StatusPill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <ul className="divide-y divide-surface-border">
              {page.data?.items.map((row) => (
                <li key={row.productId} className="grid gap-3 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <strong className="text-sm">{row.productDisplayName}</strong>
                    <StatusPill tone={row.state === 'SUFFICIENT' ? 'positive' : 'danger'}>
                      {row.state === 'SUFFICIENT' ? 'Suficiente' : 'Insuficiente'}
                    </StatusPill>
                  </div>
                  <dl className="grid grid-cols-3 gap-2 text-xs">
                    <Metric label="Atual" value={row.currentQuantity} unit={units[row.baseUnit]} />
                    <Metric
                      label="Previsto"
                      value={row.plannedQuantity}
                      unit={units[row.baseUnit]}
                    />
                    <Metric
                      label="Projetado"
                      value={row.projectedQuantity}
                      unit={units[row.baseUnit]}
                    />
                  </dl>
                </li>
              ))}
            </ul>
          )}
          <Pagination
            total={total}
            limit={view.limit}
            offset={view.offset}
            onOffsetChange={(offset) => onView({ offset })}
            itemLabel={(count) => (count === 1 ? 'produto' : 'produtos')}
          />
        </div>
      )}
    </section>
  );
}

function Quantity({
  value,
  unit,
  strong = false,
}: {
  value: string;
  unit: string;
  strong?: boolean;
}) {
  return (
    <td
      className={`px-5 py-4 text-right text-sm ${strong ? 'font-semibold' : 'text-content-muted'}`}
    >
      {value} {unit}
    </td>
  );
}

function Metric({ label, value, unit }: { label: string; value: string; unit: string }) {
  return (
    <div>
      <dt className="text-content-faint">{label}</dt>
      <dd className="mt-1 font-semibold">
        {value} {unit}
      </dd>
    </div>
  );
}
